import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { emailPattern } from '@utils/patterns';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { Button } from '@components/Core/Button';
import VerificationCodeForm from '@forms/VerificationCodeForm';
import { Input } from '@inputs/Input';
import { ChangeProfileNamePopupTransFields } from '@popups/ChangeProfileNamePopup/types';
import { changeProfileNamePopupTranslations } from '@popups/ChangeProfileNamePopup/translations';
import {
  ChangeProfileEmailPopupStepType,
  ChangeProfileEmailPopupTransFields,
  IChangeProfileEmailPopupForm,
} from './types';
import { changeProfileEmailPopupTranslations } from './translations';

export const ChangeProfileEmailPopup: FC = () => {
  const {
    popupStore: { closePopup },
    profileStore: { changeEmail, verifyNewEmail, veriifyExistingEmail },
    authStore: { normalizeProfile },
    alertStore: { showAlert },
  } = useStore();

  const [step, setStep] = useState<ChangeProfileEmailPopupStepType>('changeEmail');

  const useFormMethods = useForm<IChangeProfileEmailPopupForm>();

  useEffect(() => {
    setStep('changeEmail');
  }, []);

  const {
    handleSubmit,
    getValues,
    formState: { isDirty },
  } = useFormMethods;

  const { t } = useMyTranslations<
    ChangeProfileEmailPopupTransFields | Extract<ChangeProfileNamePopupTransFields, 'Cancel'>
  >([changeProfileEmailPopupTranslations.name, changeProfileNamePopupTranslations.name]);

  const { mutate, isPending } = useMutation({
    mutationFn: changeEmail,
    mutationKey: ['changeEmail'],
    onSuccess: () => {
      setStep('veriifyExistingEmail');
    },
  });

  const { mutate: veriifyExistingEmailMutation } = useMutation({
    mutationFn: veriifyExistingEmail,
    mutationKey: ['veriifyExistingEmail'],
    onSuccess: () => {
      setStep('verifyNewEmail');
    },
  });

  const { mutate: verifyNewEmailMutation } = useMutation({
    mutationFn: verifyNewEmail,
    mutationKey: ['verifyNewEmail'],
    onSuccess: (res) => {
      closePopup();
      normalizeProfile(res);
      showAlert('Your email was changed succesfully', 'success');
    },
  });

  const onSubmit = useCallback(({ email }: IChangeProfileEmailPopupForm) => {
    mutate(email);
  }, []);

  const onCodeSubmit = useCallback(
    (code: string) => {
      if (step === 'veriifyExistingEmail') veriifyExistingEmailMutation(code);
      if (step === 'verifyNewEmail') verifyNewEmailMutation({ email: getValues('email'), code });
    },
    [step]
  );

  if (step === 'changeEmail')
    return (
      <div className="flex flex-col justify-stretch items-center gap-y-[16px]">
        <p className="default-text mb-[8px] text-black text-center">{t('Edit Email')}</p>
        <FormProvider {...useFormMethods}>
          <Input
            name="email"
            styleProp="mb-[16px] h-[40px]"
            required
            registerPattern={emailPattern}
          />
        </FormProvider>
        <Button
          text={t('Continue')}
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || isPending}
        />
        <Button
          text={t('Cancel', { ns: changeProfileNamePopupTranslations.name })}
          onClick={closePopup}
          styleType="secondary"
        />
      </div>
    );
  return (
    <div className="flex flex-col justify-stretch items-center gap-y-[16px]">
      <p className="default-text mb-[8px] text-black text-center">
        {t(step === 'veriifyExistingEmail' ? 'Сonfirm your old Email' : 'Сonfirm your new Email')}
      </p>
      <VerificationCodeForm onSubmit={onCodeSubmit} />
      <Button
        text={t('Cancel', { ns: changeProfileNamePopupTranslations.name })}
        onClick={closePopup}
        styleType="secondary"
      />
    </div>
  );
};
